/**
 * Datos del diagrama de un modelo semántico: carga desde la API, armado de
 * nodos (tablas del modelo) y aristas (joins reales) y acomodo con dagre.
 *
 * Formato real de los datos (WHISPERBI_SEMANTIC_*_V2):
 * - modelo: `semantid_database_id` (sic, con la errata del backend) y
 *   `contained_tables_id: [{ table_id }]`.
 * - tabla: `table_id`, `table_name`, `display_name`, `columns[]`
 *   (`column_name`, `data_type`, `business_name`, `semantic_type`, ...) y
 *   `joins[]`: `{ join_id, target_table, type, cardinality, purpose,
 *   on: [{ source_column, target_column }] }`, donde `target_table` es el
 *   `table_name` de la tabla destino (no su id).
 */
import dagre from 'dagre';
import SemanticModelService from '../../services/SemanticModelService';
import SemanticTableService from '../../services/SemanticTableService';

export const NODE_WIDTH = 280;
const HEADER_HEIGHT = 58;
const ROW_HEIGHT = 24;
const FOOTER_HEIGHT = 26;
// Columnas visibles en el nodo; el resto se ve en el panel de detalle.
export const MAX_VISIBLE_COLUMNS = 8;
const PAGE_SIZE = 100;
const MAX_PAGES = 50;

const unwrap = (response) => response?.data && !Array.isArray(response.data) ? response.data : response;

export const tableLabel = (table) => table.display_name || table.table_name || table.table_id;

/** Ids de las tablas del modelo, en el orden guardado. */
export const containedTableIds = (model) => (model?.contained_tables_id || [])
  .map((entry) => (typeof entry === 'string' ? entry : entry?.table_id))
  .filter(Boolean)
  .map(String);

/**
 * Carga el modelo y SOLO sus tablas: lista paginada de la database del
 * modelo, filtrada por `contained_tables_id`; las que no aparezcan ahí (p.
 * ej. de otra database) se piden una por una.
 */
export async function loadModelGraphSource(modelId) {
  const model = unwrap(await SemanticModelService.getById(modelId));
  if (!model) {
    throw new Error('No se encontró el modelo semántico.');
  }
  const wanted = containedTableIds(model);
  const wantedSet = new Set(wanted);
  const found = new Map();

  const databaseId = model.semantid_database_id || model.semantic_database_id;
  if (databaseId && wanted.length > 0) {
    let nextToken;
    for (let page = 0; page < MAX_PAGES; page += 1) {
      // eslint-disable-next-line no-await-in-loop
      const response = await SemanticTableService.list({ semanticDatabaseId: databaseId, limit: PAGE_SIZE, nextToken });
      const items = Array.isArray(response) ? response : (response?.items || []);
      items.forEach((table) => {
        const id = String(table.table_id);
        if (wantedSet.has(id)) found.set(id, table);
      });
      nextToken = Array.isArray(response) ? null : (response?.next_cursor || response?.next_token);
      if (!nextToken || found.size === wantedSet.size) break;
    }
  }

  const missing = wanted.filter((id) => !found.has(id));
  const fetched = await Promise.all(missing.map((id) => SemanticTableService.getById(id)
    .then(unwrap)
    .catch(() => null)));
  fetched.forEach((table, index) => {
    if (table && table.table_id) found.set(missing[index], table);
  });

  const tables = wanted.map((id) => found.get(id)).filter(Boolean);
  return { model, tables, missingTableIds: wanted.filter((id) => !found.has(id)) };
}

const cardinalityLabel = (cardinality) => ({
  many_to_one: 'N:1',
  one_to_many: '1:N',
  one_to_one: '1:1',
  many_to_many: 'N:N'
}[cardinality] || cardinality || '');

export const nodeHeight = (table) => {
  const columns = (table.columns || []).length;
  const visible = Math.min(columns, MAX_VISIBLE_COLUMNS);
  return HEADER_HEIGHT + visible * ROW_HEIGHT + (columns > MAX_VISIBLE_COLUMNS ? FOOTER_HEIGHT : 8);
};

/**
 * Nodos = tablas del modelo; aristas = joins cuyo destino también está en
 * el modelo. Los joins hacia tablas fuera del modelo no se dibujan: quedan
 * en `externalJoins` para mostrarlos en el detalle de la tabla origen.
 */
export function buildModelGraph(tables) {
  const byName = new Map();
  tables.forEach((table) => {
    if (table.table_name) byName.set(table.table_name, table);
  });

  const joinColumnsByTable = new Map(tables.map((table) => [table.table_id, new Set()]));
  const edges = [];
  const externalJoins = [];

  tables.forEach((source) => {
    (source.joins || []).forEach((join, index) => {
      const target = byName.get(join.target_table);
      const on = Array.isArray(join.on) ? join.on : [];
      if (!target) {
        externalJoins.push({ sourceTableId: source.table_id, join });
        return;
      }
      on.forEach((pair) => {
        joinColumnsByTable.get(source.table_id)?.add(pair.source_column);
        joinColumnsByTable.get(target.table_id)?.add(pair.target_column);
      });
      edges.push({
        id: `join-${source.table_id}-${join.join_id || index}`,
        source: `tbl-${source.table_id}`,
        target: `tbl-${target.table_id}`,
        type: 'smoothstep',
        label: cardinalityLabel(join.cardinality),
        data: { join, sourceTable: source, targetTable: target }
      });
    });
  });

  const nodes = tables.map((table) => ({
    id: `tbl-${table.table_id}`,
    type: 'modelTable',
    position: { x: 0, y: 0 },
    data: { table, joinColumns: joinColumnsByTable.get(table.table_id) || new Set() },
    width: NODE_WIDTH,
    height: nodeHeight(table)
  }));

  return { nodes, edges, externalJoins };
}

const GRID_GAP_X = 48;
const GRID_GAP_Y = 40;

/**
 * Cuadrícula para tablas sin relaciones: dagre las pondría todas en el
 * mismo rango, una sola fila/columna kilométrica (un modelo de 14 tablas
 * sin joins quedaba de 280 x 3558 px). Más ancha que alta, como la pantalla.
 */
function gridLayout(nodes, originX, originY) {
  const columns = Math.max(1, Math.ceil(Math.sqrt(nodes.length * 1.6)));
  const positions = new Map();
  let y = originY;
  for (let start = 0; start < nodes.length; start += columns) {
    const row = nodes.slice(start, start + columns);
    const rowY = y;
    row.forEach((node, index) => {
      positions.set(node.id, { x: originX + index * ((node.width || NODE_WIDTH) + GRID_GAP_X), y: rowY });
    });
    y += Math.max(...row.map((node) => node.height || 120)) + GRID_GAP_Y;
  }
  return positions;
}

/**
 * Acomoda los nodos. Las tablas con relaciones van con dagre (`direction`:
 * 'LR' horizontal o 'TB' vertical); las que no tienen ninguna, en una
 * cuadrícula debajo. Devuelve nodos nuevos con `position` (esquina
 * superior izquierda, como espera React Flow) y los handles del lado
 * correcto.
 */
export function layoutModelGraph(nodes, edges, direction = 'LR') {
  const connectedIds = new Set(edges.flatMap((edge) => [edge.source, edge.target]));
  const connected = nodes.filter((node) => connectedIds.has(node.id));
  const isolated = nodes.filter((node) => !connectedIds.has(node.id));
  const positions = new Map();

  let bottom = 0;
  if (connected.length > 0) {
    const graph = new dagre.graphlib.Graph();
    graph.setGraph({ rankdir: direction, nodesep: 48, ranksep: 110, marginx: 24, marginy: 24 });
    graph.setDefaultEdgeLabel(() => ({}));
    connected.forEach((node) => graph.setNode(node.id, { width: node.width || NODE_WIDTH, height: node.height || 120 }));
    edges.forEach((edge) => graph.setEdge(edge.source, edge.target));
    dagre.layout(graph);
    connected.forEach((node) => {
      const placed = graph.node(node.id);
      const width = node.width || NODE_WIDTH;
      const height = node.height || 120;
      const position = { x: placed.x - width / 2, y: placed.y - height / 2 };
      positions.set(node.id, position);
      bottom = Math.max(bottom, position.y + height);
    });
  }
  gridLayout(isolated, 24, connected.length > 0 ? bottom + GRID_GAP_Y * 2 : 24)
    .forEach((position, id) => positions.set(id, position));

  const horizontal = direction === 'LR';
  return nodes.map((node) => ({
    ...node,
    position: positions.get(node.id),
    data: { ...node.data, direction },
    sourcePosition: horizontal ? 'right' : 'bottom',
    targetPosition: horizontal ? 'left' : 'top'
  }));
}

/** Etiquetas legibles del join (para el panel de detalle). */
export const describeJoin = (join) => ({
  type: { left: 'LEFT JOIN', inner: 'INNER JOIN', right: 'RIGHT JOIN', full: 'FULL JOIN' }[join.type] || (join.type || '').toUpperCase(),
  cardinality: {
    many_to_one: 'Muchos a uno (N:1)',
    one_to_many: 'Uno a muchos (1:N)',
    one_to_one: 'Uno a uno (1:1)',
    many_to_many: 'Muchos a muchos (N:N)'
  }[join.cardinality] || join.cardinality || '—'
});

export const JOIN_TYPES = [
  { value: 'left', label: 'LEFT JOIN', help: 'Todas las filas del origen, con o sin coincidencia' },
  { value: 'inner', label: 'INNER JOIN', help: 'Solo las filas que coinciden en ambas tablas' },
  { value: 'right', label: 'RIGHT JOIN', help: 'Todas las filas del destino, con o sin coincidencia' },
  { value: 'full', label: 'FULL JOIN', help: 'Todas las filas de ambas tablas' }
];

export const CARDINALITIES = [
  { value: 'many_to_one', label: 'Muchos a uno (N:1)' },
  { value: 'one_to_many', label: 'Uno a muchos (1:N)' },
  { value: 'one_to_one', label: 'Uno a uno (1:1)' },
  { value: 'many_to_many', label: 'Muchos a muchos (N:N)' }
];

const looksLikeKey = (name) => /(^id$|_id$|id$|_key$|_hk$|^hk_|_sk$)/i.test(name);

/**
 * Pares de columnas sugeridos para un join nuevo: las que se llaman igual
 * en ambas tablas (sin distinguir mayúsculas), primero las que parecen
 * llaves (id, *_id, *_key, hk_*...). Si ninguna parece llave, sugiere solo
 * la primera coincidencia, para no proponer un join por "nombre" o "fecha".
 */
export function suggestColumnPairs(sourceTable, targetTable) {
  const targetByLower = new Map((targetTable?.columns || []).map((c) => [String(c.column_name).toLowerCase(), c.column_name]));
  const common = (sourceTable?.columns || [])
    .map((c) => c.column_name)
    .filter((name) => targetByLower.has(String(name).toLowerCase()))
    .map((name) => ({ source_column: name, target_column: targetByLower.get(String(name).toLowerCase()) }));
  const keys = common.filter((pair) => looksLikeKey(pair.source_column));
  if (keys.length) return keys;
  return common.slice(0, 1);
}

/** Payload del join en el formato que guarda semantic_table. */
export function buildJoinPayload({ targetTable, type, cardinality, pairs, purpose }) {
  const payload = {
    target_table: targetTable.table_name,
    type: type || 'left',
    on: pairs
      .filter((pair) => pair.source_column && pair.target_column)
      .map((pair) => ({ source_column: pair.source_column, target_column: pair.target_column }))
  };
  if (targetTable.schema) payload.target_schema = targetTable.schema;
  if (targetTable.database) payload.target_database = targetTable.database;
  if (cardinality) payload.cardinality = cardinality;
  if (purpose && purpose.trim()) payload.purpose = purpose.trim();
  return payload;
}

/** "HTTP 403: {"message": "..."}" -> "..." (mensaje del backend). */
export function apiErrorMessage(error, fallback) {
  const raw = error?.message || '';
  const match = raw.match(/^HTTP (\d+): (.*)$/s);
  if (match) {
    if (match[1] === '403') return 'No tienes permiso para editar las relaciones de este modelo.';
    try {
      const parsed = JSON.parse(match[2]);
      if (parsed?.message) return parsed.message;
    } catch {
      // cuerpo no JSON
    }
  }
  return raw || fallback;
}
